<template>
	<aside class="shorts-panel">
		<header class="shorts-panel__header">
			<h2 class="shorts-panel__title">
				<template v-if="mode === 'comments'">
					{{ t("square.detail.comments") }} {{ commentTotal }}
				</template>
				<template v-else>{{ t("square.detail.shorts.description") }}</template>
			</h2>
			<button type="button" class="shorts-panel__close" @click="$emit('close')">×</button>
		</header>

		<!-- 说明 -->
		<div v-if="mode === 'desc'" class="shorts-panel__desc">
			<p class="shorts-panel__desc-text">{{ descriptionText }}</p>
		</div>

		<!-- 评论 -->
		<template v-else>
			<div class="shorts-panel__tabs">
				<button
					type="button"
					class="shorts-panel__tab"
					:class="{ 'shorts-panel__tab--active': commentTab === 'hot' }"
					@click="commentTab = 'hot'"
				>
					{{ t("square.detail.shorts.tabHot") }}
				</button>
				<button
					type="button"
					class="shorts-panel__tab"
					:class="{ 'shorts-panel__tab--active': commentTab === 'new' }"
					@click="commentTab = 'new'"
				>
					{{ t("square.detail.shorts.tabNew") }}
				</button>
			</div>

			<div class="shorts-panel__comments-body">
				<ul v-if="commentList.length" class="shorts-panel__comment-list">
					<li v-for="item in commentList" :key="item.id" class="shorts-panel__comment-item">
						<img
							:src="item.user_info?.avatar"
							:alt="item.user_info?.nickname"
							class="shorts-panel__comment-avatar"
						/>
						<div class="shorts-panel__comment-content">
							<div class="shorts-panel__comment-head">
								<span class="shorts-panel__comment-user">
									@{{ item.user_info?.nickname }}·{{ item.format_time }}
								</span>
							</div>
							<p class="shorts-panel__comment-text">{{ item.reply }}</p>
							<div class="shorts-panel__comment-actions">
								<button
									type="button"
									class="shorts-panel__comment-zan"
									:disabled="isCommentZanLoading(item.id)"
									@click.stop="handleCommentZan(item)"
								>
									<img :src="item.is_zan ? zanAfterIcon : zanBeforeIcon" alt="" />
									<span>{{ item.zan || 0 }}</span>
								</button>
								<button
									type="button"
									class="shorts-panel__comment-reply"
									@click.stop="toggleReplyInput(item)"
								>
									<img :src="pinlunIcon" alt="" />
								</button>
							</div>
							<div
								v-if="String(activeReplyId) === String(item.id)"
								class="shorts-panel__reply-input"
							>
								<input
									v-model="activeReplyDraft"
									type="text"
									:placeholder="
										t('square.detail.replyPlaceholder', {
											nickname: item.user_info?.nickname || '',
										})
									"
									maxlength="500"
									:disabled="replySubmitting"
									@keyup.enter="submitReplyComment(item)"
								/>
								<button
									type="button"
									class="shorts-panel__send"
									:disabled="replySubmitting || !activeReplyDraft.trim()"
									@click="submitReplyComment(item)"
								>
									{{ replySubmitting ? t("square.detail.sending") : t("square.detail.send") }}
								</button>
							</div>
							<button
								v-if="item.reply_count > 0"
								type="button"
								class="shorts-panel__view-replies"
								:disabled="getReplyState(item.id).loading"
								@click="toggleViewReplies(item)"
							>
								<template v-if="getReplyState(item.id).loading">{{
									t("square.detail.loading")
								}}</template>
								<template v-else-if="getReplyState(item.id).expanded">{{
									t("square.detail.collapseReplies")
								}}</template>
								<template v-else
									>{{ t("square.detail.viewReplies", { count: item.reply_count }) }} &gt;</template
								>
							</button>
							<ul
								v-if="getReplyState(item.id).expanded && getReplyState(item.id).list.length"
								class="shorts-panel__reply-list"
							>
								<li
									v-for="reply in getReplyState(item.id).list"
									:key="reply.id"
									class="shorts-panel__reply-item"
								>
									<img
										:src="reply.user_info?.avatar"
										:alt="reply.user_info?.nickname"
										class="shorts-panel__reply-avatar"
									/>
									<div class="shorts-panel__reply-body">
										<span class="shorts-panel__comment-user">
											@{{ reply.user_info?.nickname }}·{{ reply.format_time }}
										</span>
										<p class="shorts-panel__comment-text">{{ reply.reply }}</p>
										<button
											type="button"
											class="shorts-panel__comment-zan"
											:disabled="isCommentZanLoading(reply.id)"
											@click.stop="handleCommentZan(reply)"
										>
											<img :src="reply.is_zan ? zanAfterIcon : zanBeforeIcon" alt="" />
											<span>{{ reply.zan || 0 }}</span>
										</button>
									</div>
								</li>
							</ul>
						</div>
					</li>
				</ul>
				<p v-else-if="!commentLoading" class="shorts-panel__empty">
					{{ t("square.detail.emptyComments") }}
				</p>
				<button
					v-if="hasMoreComments"
					type="button"
					class="shorts-panel__load-more"
					:disabled="commentLoading"
					@click="loadMoreComments"
				>
					{{ commentLoading ? t("square.detail.loading") : t("square.detail.loadMoreComments") }}
				</button>
			</div>

			<div class="shorts-panel__input-bar">
				<div class="shorts-panel__input-wrap">
					<img :src="writeIcon" alt="" class="shorts-panel__write-icon" aria-hidden="true" />
					<input
						v-model="mainCommentText"
						type="text"
						:placeholder="t('square.detail.commentPlaceholder')"
						maxlength="500"
						:disabled="commentSubmitting"
						@keyup.enter="submitMainComment"
					/>
					<div
						class="shorts-panel__send-btn"
						:disabled="commentSubmitting || !mainCommentText.trim()"
						@click="submitMainComment"
					>
						<img :src="sendIcon" alt="" />
					</div>
				</div>
			</div>
		</template>
	</aside>
</template>

<script setup>
import { computed, ref } from "vue";
import { useI18n } from "~/composables/useI18n";
import { useSquare } from "~/composables/useSquare";
import zanAfterIcon from "../../../images/zan-after.png";
import zanBeforeIcon from "../../../images/zan-before.png";
import pinlunIcon from "../../../images/pinlun.png";
import sendIcon from "../../../images/send.png";
import writeIcon from "../../../images/write.svg";

const props = defineProps({
	mode: {
		type: String,
		default: "desc",
	},
	data: {
		type: Object,
		default: () => ({}),
	},
});

defineEmits(["close"]);

const { t, locale } = useI18n();
const commentTab = ref("hot");

const {
	commentList,
	commentLoading,
	commentTotal,
	hasMoreComments,
	mainCommentText,
	activeReplyId,
	activeReplyDraft,
	commentSubmitting,
	replySubmitting,
	getReplyState,
	toggleViewReplies,
	loadMoreComments,
	toggleReplyInput,
	submitMainComment,
	submitReplyComment,
	handleCommentZan,
	isCommentZanLoading,
} = useSquare(
	() => props.data,
	() => commentTab.value
);

const descriptionText = computed(() => {
	if (!props.data) return "";
	const title = props.data.title || "";
	const desc =
		locale.value === "en"
			? props.data.content_en || props.data.desc || props.data.content || ""
			: props.data.content || props.data.desc || props.data.content_en || "";
	return desc ? `${title} | ${desc}` : title;
});
</script>

<style lang="scss" scoped>
.shorts-panel {
	display: flex;
	flex-direction: column;
	width: 429px;
	height: 905px;
	background: #ffffff;
	border: 1px solid #efefef;
	border-radius: 12px;
	overflow: hidden;
	flex-shrink: 0;

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 20px 20px 16px;
		flex-shrink: 0;
	}

	&__title {
		margin: 0;
		font-size: 22px;
		font-weight: 700;
		color: #333333;

		span {
			font-size: 18px;
			color: #666666;
		}
	}

	&__close {
		border: none;
		background: transparent;
		font-size: 24px;
		line-height: 1;
		color: #333333;
		cursor: pointer;
		padding: 0 4px;
	}

	&__desc {
		flex: 1;
		overflow-y: auto;
		padding: 0 20px 20px;
	}

	&__desc-text {
		margin: 0;
		font-size: 18px;
		color: #333333;
		line-height: 1.8;
		word-break: break-word;
	}

	&__tabs {
		display: flex;
		gap: 24px;
		padding: 0 20px;
		border-bottom: 1px solid #f0f0f0;
		flex-shrink: 0;
	}

	&__tab {
		border: none;
		background: transparent;
		padding: 0 0 8px;
		font-size: 15px;
		color: #999999;
		cursor: pointer;
		position: relative;

		&--active {
			color: #1f2329;
			font-weight: 600;

			&::after {
				content: "";
				position: absolute;
				left: 0;
				right: 0;
				bottom: 0;
				height: 2px;
				background: #1f2329;
				border-radius: 1px;
			}
		}
	}

	&__comments-body {
		flex: 1;
		overflow-y: auto;
		padding: 16px 20px;
		min-height: 0;
	}

	&__comment-list {
		list-style: none;
		margin: 0;
		padding: 0;
	}

	&__comment-item {
		display: flex;
		gap: 10px;
		margin-bottom: 20px;

		&:last-child {
			margin-bottom: 0;
		}
	}

	&__comment-avatar {
		width: 36px;
		height: 36px;
		border-radius: 50%;
		object-fit: cover;
		flex-shrink: 0;
		background: #eeeeee;
	}

	&__comment-content {
		flex: 1;
		min-width: 0;
	}

	&__comment-head {
		margin-bottom: 4px;
	}

	&__comment-user {
		font-size: 13px;
		color: #888888;
	}

	&__comment-text {
		margin: 0 0 8px;
		font-size: 14px;
		color: #1f2329;
		line-height: 1.6;
	}

	&__comment-actions {
		display: flex;
		align-items: center;
		gap: 16px;
	}

	&__comment-zan,
	&__comment-reply {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		border: none;
		background: transparent;
		padding: 0;
		cursor: pointer;
		font-size: 12px;
		color: #666666;

		&:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}

		img {
			width: 16px;
			height: 16px;
			object-fit: contain;
		}
	}

	&__reply-input {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-top: 10px;

		input {
			flex: 1;
			min-width: 0;
			height: 32px;
			padding: 0 12px;
			border: none;
			border-radius: 16px;
			background: #f5f5f5;
			font-size: 13px;
			outline: none;
		}
	}

	&__send {
		flex-shrink: 0;
		height: 28px;
		padding: 0 12px;
		border: none;
		border-radius: 14px;
		background: #f2af19;
		color: #ffffff;
		font-size: 12px;
		cursor: pointer;

		&:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}
	}

	&__view-replies {
		border: none;
		background: transparent;
		padding: 0;
		margin-top: 8px;
		font-size: 12px;
		color: #999999;
		cursor: pointer;

		&:disabled {
			cursor: not-allowed;
		}
	}

	&__reply-list {
		list-style: none;
		margin: 10px 0 0;
		padding: 0 0 0 10px;
		border-left: 2px solid #f0f0f0;
	}

	&__reply-item {
		display: flex;
		gap: 8px;
		margin-bottom: 12px;

		&:last-child {
			margin-bottom: 0;
		}
	}

	&__reply-avatar {
		width: 28px;
		height: 28px;
		border-radius: 50%;
		object-fit: cover;
		flex-shrink: 0;
	}

	&__reply-body {
		flex: 1;
		min-width: 0;
	}

	&__empty {
		margin: 0;
		padding: 40px 0;
		text-align: center;
		font-size: 14px;
		color: #999999;
	}

	&__load-more {
		display: block;
		width: 100%;
		margin-top: 12px;
		padding: 8px 0;
		border: none;
		background: transparent;
		font-size: 13px;
		color: #666666;
		cursor: pointer;

		&:disabled {
			cursor: not-allowed;
			color: #bbbbbb;
		}
	}

	&__input-bar {
		padding: 12px 16px 16px;
		flex-shrink: 0;
	}

	&__input-wrap {
		display: flex;
		align-items: center;
		gap: 8px;
		height: 44px;
		padding: 0 6px 0 14px;
		background: #f5f5f5;
		border-radius: 22px;

		input {
			flex: 1;
			min-width: 0;
			height: 100%;
			border: none;
			background: transparent;
			font-size: 14px;
			color: #333333;
			outline: none;

			&::placeholder {
				color: #bbbbbb;
			}

			&:disabled {
				cursor: not-allowed;
			}
		}
	}

	&__write-icon {
		flex-shrink: 0;
		width: 16px;
		height: 16px;
		object-fit: contain;
	}

	&__send-btn {
		width: 32px;
		height: 32px;
		cursor: pointer;

		&:disabled {
			opacity: 0.4;
			cursor: not-allowed;
		}

		img {
			width: 100%;
			height: 100%;
			object-fit: contain;
		}
	}
}
</style>
